import React, { ForwardRefExoticComponent, RefAttributes, forwardRef, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';
import type { RootState } from 'store/index';
import { DefaultRootStateProps, LinkTarget, NavItemType } from 'types';
import { companyAttendanceEnabled } from 'utils/companyAttendance';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export interface NavItemProps {
    item: NavItemType;
    level: number;
}

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }: NavItemProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const isOpen = useSelector((state: DefaultRootStateProps) => state.customization.isOpen);
    const borderRadius = useSelector((state: DefaultRootStateProps) => state.customization.borderRadius);
    // Re-subscribe when user loads so attendance visibility re-evaluates.
    useSelector((state: RootState) => state.user?.info?.nkObject?.attendance ?? 0);

    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const isSelected = useMemo(() => isOpen.findIndex((id) => id === item?.id) > -1, [isOpen, item?.id]);

    const Icon = item?.icon!;
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: isSelected ? 8 : 6,
                height: isSelected ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget: LinkTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    const LinkComponent = useMemo(
        () => forwardRef<HTMLAnchorElement, any>((props, ref) => <Link ref={ref} {...props} to={item.url!} target={itemTarget} />),
        [item.url, itemTarget]
    );

    let listItemProps: {
        component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
        href?: string;
        target?: LinkTarget;
    } = { component: LinkComponent };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = useCallback(() => {
        dispatch({ type: MENU_OPEN, id: item.id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    }, [dispatch, item.id, matchesSM]);

    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
    }, [dispatch, item.id]);

    const hideAttendance = item.id === 'attendance' && !companyAttendanceEnabled();
    if ((item as any).hidden || hideAttendance) return null;

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                py: level > 1 ? 1 : 1.25,
                pl: `${level * 24}px`
            }}
            selected={isSelected}
            onClick={itemHandler}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>{itemIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                    sx={item.chip.sx}
                />
            )}
        </ListItemButton>
    );
};

export default React.memo(NavItem);
